import React, { useState } from 'react';
import { Droppable } from '@hello-pangea/dnd';
import { Leg, LegNode, LegNodeTemplate, Client, isDeliveryNodeType } from '../../types';
import LegNodeConfigModal from './LegNodeConfigModal';
import CsvDownloadConfigModal from './CsvDownloadConfigModal';
import CloudDataDestinationConfigModal from './CloudDataDestinationConfigModal';
import GoogleSheetsConfigModal from './GoogleSheetsConfigModal';
import SftpConfigModal from './SftpConfigModal';

interface Props {
  leg: Leg;
  nodes: LegNode[];                 // only the nodes in THIS leg
  templates: LegNodeTemplate[];     // for the inline child-node picker
  onAddNode: (template: LegNodeTemplate, parentNodeId?: string) => void;
  onRemoveNode: (nodeId: string) => void;
  onUpdateNodeConfig: (nodeId: string, config: Record<string, any>) => void;
  /** Account client directory, used to turn node.clientId into a name. */
  clients: Client[];
  /** Called after a client is assigned inside a node's config modal. */
  onClientAssigned: () => void;
  onRename: (name: string) => void;
  onToggleActive: () => void;
  onDelete: () => void;
  onCopyIn: () => void;
  onCopyOut: (targetDataBodyId: string) => Promise<void>;
  loadCopyTargets: () => Promise<CopyTarget[]>;
}

type CopyTarget = { id: string; name: string; status: 'active' | 'paused' | 'archived' };

/**
 * One leg = one named container of leg_nodes that runs as its own decision
 * tree. Multiple legs sit side-by-side in LegsSection and run in parallel
 * when a lead arrives.
 */
function LegContainer({
  leg,
  nodes,
  templates,
  onAddNode,
  onRemoveNode,
  onUpdateNodeConfig,
  clients,
  onClientAssigned,
  onRename,
  onToggleActive,
  onDelete,
  onCopyIn,
  onCopyOut,
  loadCopyTargets,
}: Props) {
  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState(leg.name);
  const [showMenu, setShowMenu] = useState(false);
  const [configModalNode, setConfigModalNode] = useState<LegNode | null>(null);

  // "Copy Leg Out" picker state.
  const [showCopyOut, setShowCopyOut] = useState(false);
  const [copyTargets, setCopyTargets] = useState<CopyTarget[] | null>(null);
  const [copyTargetsError, setCopyTargetsError] = useState(false);
  const [copyingTo, setCopyingTo] = useState<string | null>(null);
  const [copiedTo, setCopiedTo] = useState<string | null>(null);

  function openCopyOut() {
    setShowMenu(false);
    setShowCopyOut(true);
    setCopiedTo(null);
    setCopyTargetsError(false);
    setCopyTargets(null);
    loadCopyTargets()
      .then(setCopyTargets)
      .catch(() => { setCopyTargetsError(true); setCopyTargets([]); });
  }

  async function handleCopyOut(target: CopyTarget) {
    setCopyingTo(target.id);
    try {
      await onCopyOut(target.id);
      setCopiedTo(target.name);
    } catch {
      setCopyTargetsError(true);
    } finally {
      setCopyingTo(null);
    }
  }

  const rootNodes = nodes.filter(n => n.parentNodeId === null);
  const totalNodes = nodes.length;

  function getLegEmoji(type: string): string {
    const map: Record<string, string> = {
      webhook_output: '📤',
      custom_api: '🔌',
      time_delay: '⏰',
      condition: '🔀',
      split: '🔁',
      csv_download: '⬇',
      cloud_data_destination: '☁',
      google_sheets: '📊',
    };
    return map[type] || '⚙';
  }

  function commitRename() {
    const trimmed = nameDraft.trim();
    if (trimmed && trimmed !== leg.name) {
      onRename(trimmed);
    } else {
      setNameDraft(leg.name);
    }
    setEditingName(false);
  }

  function cancelRename() {
    setNameDraft(leg.name);
    setEditingName(false);
  }

  function confirmDelete() {
    setShowMenu(false);
    if (window.confirm(`Delete "${leg.name}"? All ${totalNodes} node${totalNodes === 1 ? '' : 's'} inside this leg will be removed.`)) {
      onDelete();
    }
  }

  function renderNode(node: LegNode, depth = 0) {
    const children = nodes.filter(n => n.parentNodeId === node.id);
    // Show WHO this destination delivers to right on the card, so a leg can
    // be scanned without opening every node. Only delivery nodes have one;
    // a delivery node with no client is a legacy node from before clients
    // existed and is called out rather than left blank.
    const isDelivery = isDeliveryNodeType(node.type);
    const client = node.clientId ? clients.find(c => c.id === node.clientId) : undefined;
    return (
      <div key={node.id} style={{ marginLeft: depth * 24 }}>
        <div className="leg-node-card">
          <div className="leg-node-icon">{getLegEmoji(node.type)}</div>
          <div className="leg-node-info" style={{ cursor: 'pointer', minWidth: 0 }} onClick={() => setConfigModalNode(node)}>
            <div className="leg-node-name">{node.name}</div>
            {isDelivery && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 4,
                  fontSize: 12,
                  fontWeight: 600,
                  color: client ? '#1d4ed8' : '#b45309',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
                title={client ? `Delivering to ${client.name}` : 'No client assigned to this destination'}
              >
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/>
                  <circle cx="9.5" cy="7" r="4"/>
                  <path d="M22 11h-6"/>
                  <path d="M19 8l3 3-3 3"/>
                </svg>
                {client ? client.name : 'No client'}
              </div>
            )}
            <div className="leg-node-type">
              {node.type.replace(/_/g, ' ')}
              {node.config?.webhookUrl && ` → ${node.config.webhookUrl.substring(0, 30)}...`}
              {node.config?.delayMinutes && ` (${node.config.delayMinutes} min)`}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 4 }}>
            <button
              className="btn btn-secondary btn-sm"
              onClick={() => {
                const type = prompt('Add child node type: webhook_output, custom_api, time_delay, condition, split');
                if (type) {
                  const template = templates.find(t => t.type === type);
                  if (template) {onAddNode(template, node.id);}
                }
              }}
              title="Add child node"
            >+</button>
            <button
              className="btn-icon"
              onClick={() => onRemoveNode(node.id)}
              style={{ color: '#ef4444' }}
              title="Remove node"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18"/>
                <line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </button>
          </div>
        </div>
        {children.map(child => renderNode(child, depth + 1))}
      </div>
    );
  }

  return (
    <div
      className="leg-column"
      style={{
        opacity: leg.isActive ? 1 : 0.55,
        borderColor: leg.isActive ? undefined : '#e5e7eb',
      }}
    >
      {/* Header */}
      <div className="leg-column-header">
        {editingName ? (
          <input
            className="leg-column-name-input"
            value={nameDraft}
            onChange={e => setNameDraft(e.target.value)}
            onBlur={commitRename}
            onKeyDown={e => {
              if (e.key === 'Enter') {commitRename();}
              if (e.key === 'Escape') {cancelRename();}
            }}
            autoFocus
          />
        ) : (
          <div
            className="leg-column-name"
            onDoubleClick={() => setEditingName(true)}
            title="Double-click to rename"
          >
            {leg.name}
          </div>
        )}

        {/* On/off toggle */}
        <label
          className="leg-toggle"
          title={leg.isActive ? 'Active — leads route through this leg' : 'Inactive — leg is skipped'}
        >
          <input type="checkbox" checked={leg.isActive} onChange={onToggleActive} />
          <span className="leg-toggle-slider" />
        </label>

        {/* ⋮ menu */}
        <div style={{ position: 'relative' }}>
          <button
            className="btn-icon"
            onClick={() => setShowMenu(s => !s)}
            title="Leg menu"
            style={{ width: 28, height: 28, fontSize: 18, lineHeight: 1 }}
          >
            &#x22EE;
          </button>
          {showMenu && (
            <>
              {/* Click-outside backdrop */}
              <div
                style={{ position: 'fixed', inset: 0, zIndex: 5 }}
                onClick={() => setShowMenu(false)}
              />
              <div
                style={{
                  position: 'absolute',
                  right: 0,
                  top: '110%',
                  background: '#fff',
                  border: '1px solid #e5e7eb',
                  borderRadius: 8,
                  boxShadow: '0 8px 20px rgba(0,0,0,0.08)',
                  minWidth: 140,
                  zIndex: 10,
                  overflow: 'hidden',
                }}
              >
                <button
                  className="leg-menu-item"
                  onClick={() => { setShowMenu(false); setEditingName(true); }}
                >Rename</button>
                <button
                  className="leg-menu-item"
                  onClick={() => { setShowMenu(false); onCopyIn(); }}
                  title="Duplicate this leg inside this data body (created inactive)"
                >Copy Leg In</button>
                <button
                  className="leg-menu-item"
                  onClick={openCopyOut}
                  title="Copy this leg to another data body (created inactive)"
                >Copy Leg Out</button>
                <button
                  className="leg-menu-item"
                  style={{ color: '#dc2626' }}
                  onClick={confirmDelete}
                >Delete leg</button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Sub-header meta line */}
      <div className="leg-column-meta">
        {totalNodes === 0 ? 'no nodes — drop one here' : `${totalNodes} node${totalNodes === 1 ? '' : 's'}`}
        {!leg.isActive && ' · inactive'}
      </div>

      {/* Body / drop zone */}
      <Droppable droppableId={`leg:${leg.id}`}>
        {(provided, snapshot) => (
          <div
            ref={provided.innerRef}
            {...provided.droppableProps}
            className="leg-column-body"
            style={{
              background: snapshot.isDraggingOver ? 'rgba(99,102,241,0.05)' : undefined,
              border: snapshot.isDraggingOver ? '2px dashed #6366f1' : '2px dashed transparent',
              borderRadius: 8,
              transition: 'background 0.15s, border-color 0.15s',
            }}
          >
            {rootNodes.length === 0 ? (
              <div style={{
                padding: '24px 12px',
                textAlign: 'center',
                color: '#9ca3af',
                fontSize: 12,
                lineHeight: 1.5,
              }}>
                Drag a leg template here to wire up this leg.
              </div>
            ) : (
              rootNodes.map(node => renderNode(node))
            )}
            {provided.placeholder}
          </div>
        )}
      </Droppable>

      {/* Per-node config modal. csv_download has its own modal because its
          UI is entirely different from the other types (no destination URL,
          no field mapping — instead: filters, summary, file history). All
          other node types share LegNodeConfigModal. */}
      {configModalNode && configModalNode.type === 'csv_download' && (
        <CsvDownloadConfigModal
          legNode={configModalNode}
          onClientAssigned={onClientAssigned}
          onSave={(config) => {
            onUpdateNodeConfig(configModalNode.id, config);
            setConfigModalNode(null);
          }}
          onClose={() => setConfigModalNode(null)}
        />
      )}
      {configModalNode && configModalNode.type === 'cloud_data_destination' && (
        <CloudDataDestinationConfigModal
          legNode={configModalNode}
          onClientAssigned={onClientAssigned}
          onSave={(config) => {
            onUpdateNodeConfig(configModalNode.id, config);
            setConfigModalNode(null);
          }}
          onClose={() => setConfigModalNode(null)}
        />
      )}
      {configModalNode && configModalNode.type === 'google_sheets' && (
        <GoogleSheetsConfigModal
          legNode={configModalNode}
          onClientAssigned={onClientAssigned}
          onSave={(config) => {
            onUpdateNodeConfig(configModalNode.id, config);
            setConfigModalNode(null);
          }}
          onClose={() => setConfigModalNode(null)}
        />
      )}
      {configModalNode && configModalNode.type === 'sftp_ftp' && (
        <SftpConfigModal
          legNode={configModalNode}
          onClientAssigned={onClientAssigned}
          onSave={(config) => {
            onUpdateNodeConfig(configModalNode.id, config);
            setConfigModalNode(null);
          }}
          onClose={() => setConfigModalNode(null)}
        />
      )}
      {configModalNode && configModalNode.type !== 'csv_download' && configModalNode.type !== 'cloud_data_destination' && configModalNode.type !== 'google_sheets' && configModalNode.type !== 'sftp_ftp' && (
        <LegNodeConfigModal
          legNode={configModalNode}
          onClientAssigned={onClientAssigned}
          onSave={(config) => {
            onUpdateNodeConfig(configModalNode.id, config);
            setConfigModalNode(null);
          }}
          onClose={() => setConfigModalNode(null)}
        />
      )}

      {/* Copy Leg Out — pick a destination data body. The copy lands in the
          chosen body as an inactive leg, so the current view is unchanged. */}
      {showCopyOut && (
        <div
          onClick={() => { if (!copyingTo) {setShowCopyOut(false);} }}
          style={{
            position: 'fixed', inset: 0, zIndex: 50,
            background: 'rgba(0,0,0,0.35)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: '#fff', borderRadius: 12, width: 380, maxWidth: '90vw',
              maxHeight: '80vh', display: 'flex', flexDirection: 'column',
              boxShadow: '0 20px 50px rgba(0,0,0,0.25)', overflow: 'hidden',
            }}
          >
            <div style={{ padding: '16px 20px', borderBottom: '1px solid #eef0f2' }}>
              <div style={{ fontSize: 16, fontWeight: 600 }}>Copy “{leg.name}” to another data body</div>
              <div style={{ fontSize: 13, color: '#6b7280', marginTop: 4 }}>
                The copy is created <strong>inactive</strong> so you can adjust it before it goes live.
              </div>
            </div>

            <div style={{ padding: 12, overflowY: 'auto' }}>
              {copiedTo ? (
                <div style={{ padding: '20px 12px', textAlign: 'center' }}>
                  <div style={{ fontSize: 32, lineHeight: 1, marginBottom: 8 }}>✓</div>
                  <div style={{ fontSize: 14 }}>
                    Copied to <strong>{copiedTo}</strong> as an inactive leg.
                  </div>
                </div>
              ) : copyTargets === null ? (
                <div style={{ padding: 20, textAlign: 'center', color: '#9ca3af', fontSize: 13 }}>
                  Loading data bodies…
                </div>
              ) : copyTargets.length === 0 ? (
                <div style={{ padding: 20, textAlign: 'center', color: '#9ca3af', fontSize: 13 }}>
                  {copyTargetsError
                    ? 'Could not load your data bodies. Please try again.'
                    : 'No other data bodies to copy into.'}
                </div>
              ) : (
                <>
                  {copyTargetsError && (
                    <div style={{ padding: '8px 12px', color: '#dc2626', fontSize: 12 }}>
                      Something went wrong. Please try again.
                    </div>
                  )}

                  {(() => {
                    // Active (and paused) bodies sit up top where they're most
                    // accessible; archived bodies are grouped underneath so a
                    // long archive list doesn't bury the live ones.
                    const active = copyTargets.filter(t => t.status !== 'archived');
                    const archived = copyTargets.filter(t => t.status === 'archived');

                    const renderTarget = (t: CopyTarget) => (
                      <button
                        key={t.id}
                        className="leg-menu-item"
                        disabled={!!copyingTo}
                        onClick={() => handleCopyOut(t)}
                        style={{
                          display: 'flex', width: '100%', justifyContent: 'space-between',
                          alignItems: 'center', padding: '10px 12px', borderRadius: 8,
                          cursor: copyingTo ? 'default' : 'pointer', opacity: copyingTo && copyingTo !== t.id ? 0.5 : 1,
                        }}
                      >
                        <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          {t.name}
                          {t.status === 'paused' && (
                            <span style={{ fontSize: 11, color: '#9ca3af' }}>· paused</span>
                          )}
                        </span>
                        {copyingTo === t.id && <span style={{ fontSize: 12, color: '#6b7280' }}>Copying…</span>}
                      </button>
                    );

                    const sectionLabel = (text: string) => (
                      <div style={{
                        padding: '8px 12px 4px', fontSize: 11, fontWeight: 600,
                        letterSpacing: 0.4, textTransform: 'uppercase', color: '#9ca3af',
                      }}>{text}</div>
                    );

                    return (
                      <>
                        {active.length > 0 && (
                          <>
                            {sectionLabel('Active')}
                            {active.map(renderTarget)}
                          </>
                        )}
                        {archived.length > 0 && (
                          <>
                            <div style={{ height: 1, background: '#eef0f2', margin: '8px 0' }} />
                            {sectionLabel('Archived')}
                            {archived.map(renderTarget)}
                          </>
                        )}
                      </>
                    );
                  })()}
                </>
              )}
            </div>

            <div style={{ padding: '12px 20px', borderTop: '1px solid #eef0f2', textAlign: 'right' }}>
              <button
                className="btn btn-secondary btn-sm"
                disabled={!!copyingTo}
                onClick={() => setShowCopyOut(false)}
              >{copiedTo ? 'Done' : 'Cancel'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default LegContainer;
